import { useCallback, useEffect, useRef } from "react";

const useDebouncedCallback = <T extends (...args: unknown[]) => void>(
    callback: T,
    delay: number,
) => {
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const callbackRef = useRef(callback);

    // Always keep latest callback
    useEffect(() => {
        callbackRef.current = callback;
    }, [callback]);

    const debouncedFn = useCallback(
        (...args: Parameters<T>) => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }

            timeoutRef.current = setTimeout(() => {
                callbackRef?.current?.(...args);
            }, delay);
        },
        [delay],
    );

    // Clean up on unmount
    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    return debouncedFn;
};

export { useDebouncedCallback };
